import type { Node as DiagramNode, Layer, Flow } from '../types';

interface UseFlowLayerVisibilityOptions {
  layers: Layer[];
  activeFlow: Flow | null;
  nodes: DiagramNode[];
}

export function useFlowLayerVisibility({ layers, activeFlow, nodes }: UseFlowLayerVisibilityOptions) {
  const getNodeLayerOpacity = (node: { id?: string; layerId?: string }): number => {
    // Active flow dims non-members
    if (activeFlow && node.id) {
      return activeFlow.nodeIds.includes(node.id) ? 1 : 0.55;
    }
    const layer = layers.find(l => l.id === (node.layerId ?? 'default')) ?? layers.find(l => l.id === 'default');
    if (!layer) return 1;
    if (!layer.visible) return 0;
    return layer.dimmed ? 0.2 : 1;
  };

  const isEdgeVisible = (edge: { id?: string; sourceNodeId: string; targetNodeId: string }): boolean => {
    // Active flow: all edges rendered; per-edge opacity handled via getEdgeOpacity
    if (activeFlow) return true;
    const src = nodes.find(n => n.id === edge.sourceNodeId);
    const tgt = nodes.find(n => n.id === edge.targetNodeId);
    const sl = layers.find(l => l.id === (src?.layerId ?? 'default'));
    const tl = layers.find(l => l.id === (tgt?.layerId ?? 'default'));
    return (sl?.visible ?? true) && (tl?.visible ?? true);
  };

  const getEdgeOpacity = (edgeId: string): number => {
    if (!activeFlow) return 1;
    return activeFlow.edgeIds.includes(edgeId) ? 1 : 0.55;
  };

  const isFlowEdgeAnimated = (edgeId: string): boolean => {
    if (!activeFlow) return false;
    return activeFlow.edgeIds.includes(edgeId);
  };

  return { getNodeLayerOpacity, isEdgeVisible, getEdgeOpacity, isFlowEdgeAnimated };
}
